export type AudiogramTemplate =
  | "dark-minimal"
  | "light-editorial"
  | "bold-brand";

export type WaveformStyle = "bars" | "mirrored" | "dots" | "line";

export type AspectRatio = "9:16" | "1:1" | "16:9";

export type AudiogramStatus = "generating" | "ready";

export interface AudiogramParagraph {
  id: string;
  start: number;
  end: number;
  text: string;
  words?: { word: string; start: number; end: number; confidence: number }[];
}

export interface AudiogramSubtitleStyles {
  font?: string;
  size?: number;
  color?: string;
}

export interface Audiogram {
  id: string;
  episode_id: string;
  title: string;
  quote: string;
  start_time: number;
  end_time: number;
  template_id: string;
  template: AudiogramTemplate;
  accent_color: string;
  show_name: string | null;
  guest_name: string;
  waveform_style: WaveformStyle;
  waveform_height: string;
  show_waveform: boolean;
  animated?: boolean;
  show_guest_avatar: boolean;
  show_date: boolean;
  background_image_url?: string;
  background_overlay: number;
  background_fit?: string;
  aspect_ratio: AspectRatio;
  status: AudiogramStatus;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  paragraphs?: AudiogramParagraph[];
  audio_clip_url?: string;
  subtitle_styles?: AudiogramSubtitleStyles;
}

export interface AudiogramCounts {
  total: number;
  ready: number;
  generating: number;
}

export interface AudiogramsResponse {
  audiograms: Audiogram[];
  counts: AudiogramCounts;
}

export interface TranscriptParagraph {
  id: string;
  start: number;
  end: number;
  text: string;
  speaker?: number;
}

export type BackgroundFit = "cover" | "contain" | "blur";

export interface AudiogramEditorState {
  templateId: string;
  template: AudiogramTemplate;
  quote: string;
  fontFamily: string;
  fontSize: number;
  showName: string;
  guestName: string;
  accentColor: string;
  waveformStyle: WaveformStyle;
  waveformColor: string;
  waveformHeight: "compact" | "medium" | "tall";
  showWaveform: boolean;
  animated: boolean;
  aspectRatio: AspectRatio;
  showEpisodeNumber: boolean;
  showDate: boolean;
  tags: string[];
  episodeNumber?: string;
  date?: string;
  textColor: string;
  startTime: number;
  endTime: number;
  transcriptParagraphs: TranscriptParagraph[];
  selectedParagraphIndex: number;
  backgroundImage?: string;
  backgroundImageKey?: string | null;
  backgroundOverlay: number;
  backgroundFit: BackgroundFit;
  showGuestAvatar: boolean;
}

export const FONT_OPTIONS = [
  { id: "inter", label: "Inter", family: "'Inter', sans-serif" },
  { id: "montserrat", label: "Montserrat", family: "'Montserrat', sans-serif" },
  { id: "outfit", label: "Outfit", family: "'Outfit', sans-serif" },
  { id: "bebas-neue", label: "Bebas Neue", family: "'Bebas Neue', sans-serif" },
  { id: "oswald", label: "Oswald", family: "'Oswald', sans-serif" },
  {
    id: "josefin-sans",
    label: "Josefin Sans",
    family: "'Josefin Sans', sans-serif",
  },
  {
    id: "playfair-display",
    label: "Playfair Display",
    family: "'Playfair Display', serif",
  },
  { id: "arvo", label: "Arvo", family: "'Arvo', serif" },
] as const;

export const ACCENT_PRESETS = [
  "#7C6EF5",
  "#4A9EFF",
  "#10B981",
  "#F59E0B",
  "#EF4444",
  "#EC4899",
] as const;

export const TEMPLATE_OPTIONS: {
  id: AudiogramTemplate;
  label: string;
  description: string;
  disabled?: boolean;
}[] = [
  {
    id: "dark-minimal",
    label: "Dark Minimal",
    description: "Dark background, colored waveform, quote overlay",
  },
  {
    id: "light-editorial",
    label: "Light Editorial",
    description: "White/cream, serif italic quote, monochrome waveform",
    disabled: true,
  },
  {
    id: "bold-brand",
    label: "Bold Brand",
    description: "Deep navy, photo area, accent color waveform",
    disabled: true,
  },
];
